import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  FaThumbsUp,
  FaRegThumbsUp,
  FaRegHeart,
  FaRegComment,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaReply,
  FaTrash
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/backendConfig';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ThemedNotification from './ThemedNotification';
import { trackEvent, logError } from '../utils/observability';
import { toJSDate } from '../utils/date';

// All reactions are POSITIVE + kid-friendly.
// We keep this list small and delightful so kids are nudged to encourage each other.
const GIF_REACTIONS = [
  {
    id: 'sparkles',
    label: 'Sparkle Wow',
    emoji: '✨',
    description: 'This artwork really sparkles!',
    gifUrl: '/reactions/sparkles.gif',
    iconUrl: '/icons/star.png' // Custom icon
  },
  {
    id: 'bravo',
    label: 'Big Applause',
    emoji: '👏',
    description: 'Amazing effort – bravo!',
    gifUrl: '/reactions/bravo.gif',
    iconUrl: '/icons/applause.png' // Custom icon
  },
  {
    id: 'heartburst',
    label: 'Heart Burst',
    emoji: '💖',
    description: 'This artwork warmed my heart.',
    gifUrl: '/reactions/heartburst.gif',
    iconUrl: '/icons/heart.png' // Heart icon from 1.png for Heart Burst
  },
  {
    id: 'highfive',
    label: 'Looks Amazing',
    emoji: '🙌',
    description: 'This looks amazing!',
    gifUrl: '/reactions/highfive.gif',
    iconUrl: '/icons/heart-filled.png' // Icon moved from Heart Burst to Looks Amazing
  }
];

const ArtworkReactions = ({
  artworkId,
  artistId,
  onKudosChange,
  onCommentChange,
  enableComments = true,
  listLayout = 'popover',
  variant = 'default',
  /** Smaller, quieter chrome for public profile / gallery cards */
  compact = false,
}) => {
  const [hasLiked, setHasLiked] = useState(false);
  const [userReactionType, setUserReactionType] = useState('like'); // which positive reaction this user picked
  const [kudosCount, setKudosCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMessage, setAuthModalMessage] = useState('');
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showReactionsPopover, setShowReactionsPopover] = useState(false);
  const [showInlineReactionList, setShowInlineReactionList] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [reactionList, setReactionList] = useState([]);
  const [isReactionListLoading, setIsReactionListLoading] = useState(false);
  const isPremium = variant === 'premium';
  const isDropdownLayout = listLayout === 'dropdown';
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const reactButtonRef = useRef(null);
  const viewReactionsButtonRef = useRef(null);
  const dropdownRootRef = useRef(null);
  /** Fixed-position anchor for portaled popovers (avoids overflow:hidden on modals). */
  const [gifPickerAnchor, setGifPickerAnchor] = useState(null);
  const [friendsPopoverAnchor, setFriendsPopoverAnchor] = useState(null);

  const showThemedAuthMessage = (message) => {
    setAuthModalMessage(message);
    setShowAuthModal(true);
  };

  useEffect(() => {
    console.log('ArtworkReactions useEffect triggered');
    console.log('artworkId:', artworkId);
    console.log('isAuthenticated:', isAuthenticated);
    console.log('user:', user);
    
    if (artworkId) {
      // Close any open popovers when we move to a different artwork
      setShowGifPicker(false);
      setShowReactionsPopover(false);
      setShowInlineReactionList(false);
      setShowDropdown(false);

      fetchKudosCount();
      if (enableComments) {
        fetchComments();
      }
      if (isAuthenticated && user) {
        checkUserKudos();
      }
    }
  }, [artworkId, isAuthenticated, user, enableComments]);

  useEffect(() => {
    if (!isDropdownLayout || !showDropdown) return undefined;
    const handlePointerDown = (event) => {
      if (dropdownRootRef.current && !dropdownRootRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isDropdownLayout, showDropdown]);

  useLayoutEffect(() => {
    if (!showGifPicker) {
      setGifPickerAnchor(null);
      return;
    }
    const update = () => {
      const el = reactButtonRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const estimatedPopoverH = 300;
      const placeAbove = r.top > estimatedPopoverH + 24;
      setGifPickerAnchor({
        left: r.left + r.width / 2,
        top: placeAbove ? r.top : r.bottom,
        placeAbove,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showGifPicker]);

  useLayoutEffect(() => {
    if (!showReactionsPopover) {
      setFriendsPopoverAnchor(null);
      return;
    }
    const update = () => {
      const el = viewReactionsButtonRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const estimatedPopoverH = 280;
      const placeAbove = r.top > estimatedPopoverH + 24;
      setFriendsPopoverAnchor({
        left: r.left + r.width / 2,
        top: placeAbove ? r.top : r.bottom,
        placeAbove,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showReactionsPopover]);

  // Debug render - commented out to reduce console spam
  // console.log('ArtworkReactions render state:', {
  //   artworkId,
  //   artistId,
  //   hasLiked,
  //   kudosCount,
  //   commentsCount: comments.length,
  //   isAuthenticated,
  //   user: user ? 'User exists' : 'No user'
  // });

  const fetchKudosCount = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.getKudosCount}?artworkId=${artworkId}`);
      const data = await response.json();
      if (data.success) {
        setKudosCount(data.totalReactions || 0);

        // If backend returns user's reaction type here, keep UI in sync
        if (data.userReaction) {
          setUserReactionType(data.userReaction);
          setHasLiked(true);
        }
      }
    } catch (error) {
      console.error('Error fetching kudos count:', error);
      logError(error, { where: 'ArtworkReactions.fetchKudosCount', artworkId });
    }
  };

  const fetchReactionList = async () => {
    try {
      setIsReactionListLoading(true);
      const response = await fetch(`${API_ENDPOINTS.getKudosList}?artworkId=${artworkId}`);
      const data = await response.json();
      if (data.success && Array.isArray(data.reactions)) {
        setReactionList(data.reactions);
      } else {
        setReactionList([]);
      }
    } catch (error) {
      console.error('Error fetching reaction list:', error);
      logError(error, { where: 'ArtworkReactions.fetchReactionList', artworkId });
      setReactionList([]);
    } finally {
      setIsReactionListLoading(false);
    }
  };

  const fetchComments = async () => {
    if (!enableComments) return;
    try {
      const response = await fetch(`${API_ENDPOINTS.getComments}?artworkId=${artworkId}`);
      const data = await response.json();
      if (data.success) {
        setComments(data.comments || []);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      logError(error, { where: 'ArtworkReactions.fetchComments', artworkId });
    }
  };

  const checkUserKudos = async () => {
    if (!user) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.getKudosCount}?artworkId=${artworkId}&userId=${user.uid || user.id}`);
      const data = await response.json();
      if (data.success && data.userReaction) {
        setHasLiked(true);
        setUserReactionType(data.userReaction || 'like');
      }
    } catch (error) {
      console.error('Error checking user kudos:', error);
      logError(error, { where: 'ArtworkReactions.checkUserKudos', artworkId, userId: user?.uid || null });
    }
  };

  const submitReaction = async (reactionType = 'like', e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('ArtworkReactions: handleKudos called');
    console.log('ArtworkReactions: isAuthenticated:', isAuthenticated);
    console.log('ArtworkReactions: user:', user);
    console.log('ArtworkReactions: artworkId:', artworkId);
    console.log('ArtworkReactions: artistId:', artistId);
    
    // Fallback: try to get user from localStorage if AuthContext doesn't work
    let currentUser = user;
    if (!currentUser) {
      try {
        const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
        if (userStr) {
          currentUser = JSON.parse(userStr);
          console.log('ArtworkReactions: User from localStorage:', currentUser);
        }
      } catch (error) {
        console.log('ArtworkReactions: Error getting user from storage:', error);
      }
    }

    if (!currentUser) {
      showThemedAuthMessage('Please log in to cheer for this amazing artwork!');
      return;
    }

    // If user clicks the same reaction again, deselect/remove it
    if (hasLiked && userReactionType === reactionType) {
      try {
        setIsLoading(true);
        const removeBody = {
          artworkId,
          userId: currentUser.uid || currentUser.id,
        };
        console.log('ArtworkReactions: Removing reaction with:', removeBody);
        const resp = await fetch(API_ENDPOINTS.removeKudos, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(removeBody),
        });
        const removeData = await resp.json();
        console.log('ArtworkReactions: removeKudos response:', removeData);

        if (removeData.success) {
          const newCount = Math.max(0, kudosCount - 1);
          setHasLiked(false);
          setUserReactionType('like');
          setKudosCount(newCount);
          if (onKudosChange) {
            onKudosChange(newCount);
          }
          if (isDropdownLayout) fetchReactionList();
        } else {
          showThemedAuthMessage(removeData.error || 'Unable to remove your reaction right now.');
        }
      } catch (err) {
        console.error('ArtworkReactions: Error removing kudos:', err);
        showThemedAuthMessage('Unable to remove your reaction right now.');
      } finally {
        setIsLoading(false);
        setShowGifPicker(false);
        setShowReactionsPopover(false);
      }
      return;
    }

    setIsLoading(true);
    try {
      const requestBody = {
        artworkId,
        userId: currentUser.uid || currentUser.id,
        userName: currentUser.displayName || currentUser.email || 'Anonymous User',
        reactionType: reactionType || 'like',
        artistId: artistId // Pass the artist ID for notifications
      };
      
      console.log('ArtworkReactions: Sending kudos request:', requestBody);
      console.log('ArtworkReactions: API endpoint:', API_ENDPOINTS.addKudos);
      
      const response = await fetch(API_ENDPOINTS.addKudos, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      console.log('ArtworkReactions: Kudos response:', data);
      
      if (data.success) {
        // Changing reaction just updates the type; total count increases only on first reaction
        const wasReactedBefore = hasLiked;
        const newCount = wasReactedBefore ? kudosCount : kudosCount + 1;

        setHasLiked(true);
        setUserReactionType(reactionType || 'like');
        setKudosCount(newCount);
        
        console.log('ArtworkReactions: Calling onKudosChange with:', newCount);
        if (onKudosChange) {
          onKudosChange(newCount);
        }
        if (isDropdownLayout) fetchReactionList();
        trackEvent('artwork_reacted', {
          artworkId,
          artistId,
          userId: currentUser.uid || currentUser.id,
          reactionType: reactionType || 'like',
        });
      } else {
        showThemedAuthMessage(data.error || 'Failed to send your cheer. Please try again!');
      }
    } catch (error) {
      console.error('ArtworkReactions: Error giving kudos:', error);
      showThemedAuthMessage('Failed to send your cheer. Please try again!');
      logError(error, {
        where: 'ArtworkReactions.submitReaction',
        artworkId,
        artistId,
        userId: user?.uid || null,
      });
    } finally {
      setIsLoading(false);
      setShowGifPicker(false);
    }
  };

  const handleKudos = (e) => {
    // Default simple thumbs‑up cheer
    submitReaction('like', e);
  };

  const handleAddComment = async () => {
    if (!enableComments) return;
    if (!isAuthenticated || !user) {
      showThemedAuthMessage('Please log in to share your thoughts about this artwork!');
      return;
    }

    if (!newComment.trim()) {
      showThemedAuthMessage('Please enter a comment to share your thoughts!');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.addComment, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          artworkId,
          userId: user.uid || user.id,
          userName: user.displayName || user.email || 'Anonymous User',
          commentText: newComment,
          artistId: artistId // Pass the artist ID for notifications
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setNewComment('');
        setShowCommentModal(false);
        fetchComments(); // Refresh comments
        
        // Notify parent component about comment change
        if (onCommentChange) {
          onCommentChange();
        }
        trackEvent('comment_added', {
          artworkId,
          artistId,
          userId: user.uid || user.id,
        });
      } else {
        showThemedAuthMessage(data.error || 'Failed to add comment. Please try again!');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      showThemedAuthMessage('Failed to add comment. Please try again!');
      logError(error, {
        where: 'ArtworkReactions.handleAddComment',
        artworkId,
        artistId,
        userId: user?.uid || null,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sendKudosNotification = async (artistId) => {
    try {
      // Send notification to artist about the kudos
      const notificationResponse = await fetch('/api/notifications/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipientId: artistId,
          type: 'kudos',
          message: `${user.displayName || user.email} gave kudos to your artwork!`,
          artworkId: artworkId
        }),
      });
      
      if (notificationResponse.ok) {
        console.log('Kudos notification sent to artist');
      }
    } catch (error) {
      console.error('Error sending kudos notification:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = toJSDate(timestamp);
    if (!date || Number.isNaN(date.getTime())) return 'Recently';

    const now = Date.now();
    const diffInMs = now - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    const formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return formatted === 'Invalid Date' ? 'Recently' : formatted;
  };

  const toggleDropdown = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const next = !showDropdown;
    setShowDropdown(next);
    setShowGifPicker(false);
    setShowReactionsPopover(false);
    setShowInlineReactionList(false);
    if (next) fetchReactionList();
  };

  const toggleReactionsList = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (listLayout === 'inline') {
      const next = !showInlineReactionList;
      setShowInlineReactionList(next);
      setShowReactionsPopover(false);
      setShowGifPicker(false);
      if (next) fetchReactionList();
      return;
    }
    const next = !showReactionsPopover;
    setShowReactionsPopover(next);
    setShowGifPicker(false);
    if (next) fetchReactionList();
  };

  const renderReactionListItems = () => {
    if (isReactionListLoading) {
      return (
        <p
          className={`py-6 text-center ${
            isDropdownLayout ? 'text-sm font-medium text-[#2F251B]' : 'text-xs text-[#8A7357]'
          }`}
        >
          Loading reactions…
        </p>
      );
    }
    if (!reactionList.length) {
      if (isDropdownLayout) {
        return (
          <div className="rounded-xl border border-[#f0e6d8] bg-[#fff6ec] px-3 py-4 text-center">
            <p className="text-sm font-semibold text-[#2F251B]">No reactions yet</p>
            <p className="mt-1 text-xs text-[#8A7357]">Pick an emoji below to cheer</p>
          </div>
        );
      }
      return (
        <p className="py-6 text-center text-xs text-[#8A7357]">No reactions yet.</p>
      );
    }
    return reactionList.map((item) => {
      const meta = GIF_REACTIONS.find((r) => r.id === item.reactionType);
      const emoji = meta ? meta.emoji : '🌟';
      const label = meta ? meta.label : item.reactionType;
      const iconUrl = meta ? meta.iconUrl : null;
      const gifUrl = meta ? meta.gifUrl : null;

      let displayName = item.userName || 'Artmosphere Artist';
      if (displayName && displayName.includes('@')) {
        displayName = displayName.split('@')[0];
      }

      return (
        <button
          key={item.id}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (item.userId) navigate(`/artist-public-gallery/${item.userId}`);
          }}
          className={`flex w-full items-center gap-3 rounded-xl border px-2.5 py-2.5 text-left transition ${
            isPremium || isDropdownLayout
              ? 'border-[#f0e6d8] bg-white hover:border-[#f5d99b] hover:bg-[#fff9ef]'
              : 'border-amber-100 hover:border-amber-300 hover:bg-amber-25'
          }`}
        >
          <div className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#fff6ec]">
            {iconUrl ? (
              <img
                src={iconUrl}
                alt={label}
                className="h-9 w-9 object-contain"
                onError={(event) => {
                  if (gifUrl) {
                    event.currentTarget.src = gifUrl;
                    event.currentTarget.className = 'h-full w-full object-cover';
                  } else {
                    event.currentTarget.style.display = 'none';
                  }
                }}
              />
            ) : gifUrl ? (
              <img
                src={gifUrl}
                alt={label}
                className="h-full w-full object-cover"
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <span className="text-2xl">{emoji}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-[#2F251B]">{displayName}</div>
            <div className="text-xs text-[#8A7357]">{label}</div>
            {item.timestamp && (
              <p className="mt-0.5 text-[10px] text-[#8A7357]/80">{formatTimestamp(item.timestamp)}</p>
            )}
          </div>
        </button>
      );
    });
  };

  const handleArtistNameClick = (artistId) => {
    if (artistId) {
      navigate(`/artist-public-gallery/${artistId}`);
    }
  };

  const handleArtistNameDoubleClick = (artistId) => {
    if (artistId) {
      navigate(`/artist-public-gallery/${artistId}`);
    }
  };

  const handleReply = async (commentId) => {
    if (!enableComments) return;
    if (!isAuthenticated || !user) {
      showThemedAuthMessage('Please log in to reply to comments!');
      return;
    }

    if (!replyText.trim()) {
      showThemedAuthMessage('Please enter a reply!');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.addComment, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          artworkId,
          userId: user.uid || user.id,
          userName: user.displayName || user.email || 'Anonymous User',
          commentText: replyText,
          artistId: artistId,
          parentCommentId: commentId // Add parent comment ID for replies
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setReplyText('');
        setReplyingTo(null);
        fetchComments(); // Refresh comments
        
        // Notify parent component about comment change
        if (onCommentChange) {
          onCommentChange();
        }
      } else {
        showThemedAuthMessage(data.error || 'Failed to add reply. Please try again!');
      }
    } catch (error) {
      console.error('Error adding reply:', error);
      showThemedAuthMessage('Failed to add reply. Please try again!');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteComment = async (commentId, commentUserId) => {
    if (!enableComments) return;
    if (!isAuthenticated || !user) {
      showThemedAuthMessage('Please log in to delete comments!');
      return;
    }

    // Check permissions: user can delete their own comments or if they own the artwork
    const canDelete = (user.uid || user.id) === commentUserId || (user.uid || user.id) === artistId;
    
    if (!canDelete) {
      showThemedAuthMessage('You can only delete your own comments or comments on your artwork!');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.deleteComment, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          commentId,
          userId: user.uid || user.id
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        fetchComments(); // Refresh comments
        
        // Notify parent component about comment change
        if (onCommentChange) {
          onCommentChange();
        }
      } else {
        showThemedAuthMessage(data.error || 'Failed to delete comment. Please try again!');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      showThemedAuthMessage('Failed to delete comment. Please try again!');
    } finally {
      setIsLoading(false);
    }
  };

  const reactBtnClass = isPremium
    ? 'flex items-center gap-1.5 rounded-full border border-[#f5d99b] bg-gradient-to-r from-[#fde68a] to-[#fbbf24] px-4 py-2 text-sm font-semibold text-[#2F251B] shadow-md transition hover:from-[#fcd34d] hover:to-[#f59e0b] disabled:opacity-50'
    : compact
      ? `inline-flex items-center gap-1 rounded-md border border-[#e5ddd3]/90 bg-white/80 px-2 py-[3px] text-[10px] font-medium tracking-wide text-[#5c4d3d] shadow-none transition hover:border-[#d8cfc4] hover:bg-[#faf8f5] ${
          isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        }`
      : `flex items-center gap-1 rounded-lg border border-[#e0d8cc] bg-white/90 px-2 py-1 text-xs font-medium text-[#141210] shadow-sm hover:bg-stone-50 ${
          isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        }`;

  const countBtnClass = isPremium
    ? 'flex w-full items-center justify-between gap-2 rounded-2xl border border-[#f0e6d8]/90 bg-white/70 px-3 py-2.5 text-left shadow-sm transition hover:border-[#f5d99b] hover:bg-[#fff9ef]'
    : compact
      ? 'inline-flex max-w-full items-center gap-1 rounded-md border border-[#e8e2da]/80 bg-[#faf9f7]/95 px-2 py-[3px] text-left text-[10px] font-medium text-[#5c4d3d] shadow-none transition hover:border-[#dcd4ca] hover:bg-[#f5f3ef]'
      : 'inline-flex items-center gap-1 rounded-full border border-[#e8e0d4] bg-stone-50 px-2.5 py-1 text-[11px] text-[#141210] hover:bg-stone-100';

  const renderCompactReactionPicker = () => (
    <div className="flex items-center justify-between gap-1 border-t border-[#f0e6d8] bg-[#fffdf9] px-2.5 py-2.5">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-[#8A7357]">React</span>
      <div className="flex items-center gap-0.5">
        {GIF_REACTIONS.map((reaction) => (
          <button
            key={reaction.id}
            type="button"
            disabled={isLoading}
            title={reaction.label}
            onClick={(e) => submitReaction(reaction.id, e)}
            className={`flex h-8 w-8 items-center justify-center rounded-full text-base transition ${
              userReactionType === reaction.id && hasLiked
                ? 'bg-[#fde68a]/80 ring-1 ring-[#f5d99b]'
                : 'hover:bg-[#fff6ec]'
            } ${isLoading ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            {reaction.emoji}
          </button>
        ))}
      </div>
    </div>
  );

  const renderDropdownLayout = () => (
    <div ref={dropdownRootRef} className="absolute bottom-2 right-2 z-20 flex flex-col items-end">
      {showDropdown && (
        <div
          className="mb-1.5 w-[min(17.5rem,calc(100vw-2.5rem))] overflow-hidden rounded-2xl border border-[#e8d4bc] bg-[#fffdf9] shadow-[0_12px_40px_rgba(120,72,20,0.22)]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-[#f0e6d8] bg-white px-3 py-2.5">
            <p className="text-xs font-semibold text-[#2F251B]">
              {kudosCount > 0
                ? `${kudosCount} ${kudosCount === 1 ? 'reaction' : 'reactions'}`
                : 'Reactions'}
            </p>
            <button
              type="button"
              onClick={toggleDropdown}
              className="flex h-6 w-6 items-center justify-center rounded-full text-[#8A7357] hover:bg-[#fff6ec]"
              aria-label="Close reactions"
            >
              <FaTimes className="h-3 w-3" />
            </button>
          </div>
          <div className="max-h-52 space-y-1 overflow-y-auto bg-white p-2.5 pr-1.5">
            {renderReactionListItems()}
          </div>
          {renderCompactReactionPicker()}
        </div>
      )}
      <button
        ref={reactButtonRef}
        type="button"
        onClick={toggleDropdown}
        className={`flex items-center gap-1 rounded-full border border-[#e8d4bc] bg-white px-2.5 py-1 text-[11px] font-semibold text-[#2F251B] shadow-md transition hover:bg-[#fff9ef] ${
          showDropdown ? 'ring-1 ring-[#f5d99b]/80' : ''
        }`}
        aria-expanded={showDropdown}
        aria-haspopup="listbox"
      >
        <span aria-hidden>{kudosCount > 0 ? '❤️' : '✨'}</span>
        <span>{kudosCount > 0 ? kudosCount : 'React'}</span>
        {showDropdown ? (
          <FaChevronUp className="h-2.5 w-2.5 text-[#8A7357]" aria-hidden />
        ) : (
          <FaChevronDown className="h-2.5 w-2.5 text-[#8A7357]" aria-hidden />
        )}
      </button>
    </div>
  );

  return (
    <>
    {isDropdownLayout ? (
      renderDropdownLayout()
    ) : (
    <div
      className={`relative ${
        listLayout === 'inline'
          ? 'flex w-full flex-col gap-3'
          : compact
            ? 'flex flex-col items-end gap-1'
            : 'flex flex-wrap items-center gap-2'
      }`}
    >
      {kudosCount > 0 && (
        <button
          ref={viewReactionsButtonRef}
          type="button"
          onClick={toggleReactionsList}
          className={countBtnClass}
        >
          {compact ? (
            <span className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0">
              <span className="inline-flex items-center gap-0.5 text-[#5A4632]">
                <FaRegHeart className="h-2.5 w-2.5 shrink-0 text-rose-400/75" aria-hidden />
                <span className="tabular-nums">{kudosCount}</span>
                <span className="font-normal text-[9px] text-[#8A7357]">
                  {kudosCount === 1 ? 'reaction' : 'reactions'}
                </span>
              </span>
              {listLayout === 'inline' ? (
                <span className="text-[10px] font-normal text-[#8A7357]">
                  {showInlineReactionList ? 'Hide list' : 'See who reacted'}
                </span>
              ) : (
                <span className="text-[9px] font-normal tracking-wide text-[#9a8a78]">· see who</span>
              )}
            </span>
          ) : (
            <>
              <span className="flex items-center gap-1.5 font-semibold text-[#2F251B]">
                <span aria-hidden>❤️</span>
                {kudosCount} {kudosCount === 1 ? 'reaction' : 'reactions'}
              </span>
              {listLayout === 'inline' ? (
                <span className="text-xs font-medium text-[#8A7357]">
                  {showInlineReactionList ? 'Hide list' : 'See who reacted'}
                </span>
              ) : (
                <span className="text-[10px] text-[#8A7357]">· see who</span>
              )}
            </>
          )}
        </button>
      )}

      {listLayout === 'inline' && showInlineReactionList && (
        <div className="max-h-56 space-y-2 overflow-y-auto rounded-2xl border border-[#f0e6d8]/90 bg-[#fffdf9]/90 p-2 pr-1 shadow-inner">
          {renderReactionListItems()}
        </div>
      )}

      <button
        ref={reactButtonRef}
        type="button"
        disabled={isLoading}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowGifPicker((prev) => !prev);
          setShowReactionsPopover(false);
          setShowInlineReactionList(false);
        }}
        className={reactBtnClass}
        title="Choose a fun reaction"
      >
        {compact ? (
          <>
            <FaRegThumbsUp className="h-3 w-3 shrink-0 opacity-[0.72]" aria-hidden />
            <span>React</span>
            {kudosCount > 0 && listLayout !== 'inline' && (
              <span className="ml-0.5 inline-flex min-w-[1rem] items-center justify-center rounded-full bg-[#f0ebe4]/90 px-1 text-[9px] font-medium tabular-nums text-[#5A4632]">
                {kudosCount}
              </span>
            )}
          </>
        ) : (
          <>
            <span>🌟 React</span>
            {kudosCount > 0 && listLayout !== 'inline' && (
              <span className="ml-1 inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-white/80 px-1 text-[10px] font-semibold text-[#141210]">
                {kudosCount}
              </span>
            )}
          </>
        )}
      </button>

      {/* GIF picker — portaled so parent overflow:hidden (e.g. ActivityWallModal) cannot clip it */}
      {showGifPicker &&
        gifPickerAnchor &&
        createPortal(
          <div
            className="pointer-events-auto fixed z-[10000] w-[min(18rem,calc(100vw-1.5rem))] rounded-xl border border-amber-200 bg-white p-3 shadow-2xl"
            style={{
              left: gifPickerAnchor.left,
              top: gifPickerAnchor.top,
              transform: gifPickerAnchor.placeAbove
                ? 'translate(-50%, calc(-100% - 12px))'
                : 'translate(-50%, 12px)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold text-amber-800">Choose a joyful reaction</span>
              <button
                type="button"
                className="text-xs text-amber-500 hover:text-amber-700"
                onClick={() => setShowGifPicker(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {GIF_REACTIONS.map((reaction) => (
                <button
                  key={reaction.id}
                  type="button"
                  disabled={isLoading}
                  onClick={(e) => submitReaction(reaction.id, e)}
                  className={`flex flex-col items-center justify-between gap-1 rounded-lg border p-2 text-[11px] transition-all duration-150 ${
                    userReactionType === reaction.id
                      ? 'border-amber-400 bg-amber-50 text-amber-800 shadow-sm'
                      : 'border-amber-100 bg-white text-gray-700 hover:border-amber-300 hover:bg-amber-25'
                  } ${isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  <div className="relative flex h-16 w-full items-center justify-center overflow-hidden rounded-md bg-amber-25">
                    {reaction.iconUrl ? (
                      <img
                        src={reaction.iconUrl}
                        alt={reaction.label}
                        className="h-12 w-12 object-contain"
                        onError={(event) => {
                          if (reaction.gifUrl) {
                            event.currentTarget.src = reaction.gifUrl;
                            event.currentTarget.className = 'h-full w-full object-cover';
                          } else {
                            event.currentTarget.style.display = 'none';
                          }
                        }}
                      />
                    ) : reaction.gifUrl ? (
                      <img
                        src={reaction.gifUrl}
                        alt={reaction.label}
                        className="h-full w-full object-cover"
                        onError={(event) => {
                          event.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : null}
                    <span
                      className="pointer-events-none absolute select-none text-2xl"
                      style={{ display: reaction.iconUrl || reaction.gifUrl ? 'none' : 'block' }}
                    >
                      {reaction.emoji}
                    </span>
                  </div>
                  <span className="font-semibold">{reaction.label}</span>
                  <span className="text-center text-[10px] text-gray-500">{reaction.description}</span>
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}

      {showReactionsPopover &&
        listLayout !== 'inline' &&
        friendsPopoverAnchor &&
        createPortal(
          <div
            className="pointer-events-auto fixed z-[10000] w-[min(20rem,calc(100vw-1.5rem))] rounded-2xl border border-amber-200 bg-white p-3 shadow-2xl"
            style={{
              left: friendsPopoverAnchor.left,
              top: friendsPopoverAnchor.top,
              transform: friendsPopoverAnchor.placeAbove
                ? 'translate(-50%, calc(-100% - 12px))'
                : 'translate(-50%, 12px)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold text-amber-800">Reactions from friends</span>
              {isReactionListLoading && <span className="text-[10px] text-amber-500">Loading…</span>}
            </div>
            <div className="max-h-64 space-y-2 overflow-y-auto pr-1">{renderReactionListItems()}</div>
          </div>,
          document.body
        )}

      {enableComments && (
        <button
          onClick={() => comments.length > 0 ? setShowComments(!showComments) : setShowCommentModal(true)}
          className="hover:text-amber-600 transition-colors flex items-center gap-1 text-xs text-gray-500"
        >
          <FaRegComment />
          <span>{comments.length}</span>
          {comments.length > 0 && (showComments ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />)}
        </button>
      )}

      {/* Comment Modal */}
      {enableComments && showCommentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 border border-amber-200 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-amber-800">Add a Comment</h3>
              <button
                onClick={() => setShowCommentModal(false)}
                className="text-amber-500 hover:text-amber-700"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="mb-4">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Share your thoughts about this artwork..."
                className="w-full p-3 border border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none resize-none"
                rows="3"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleAddComment}
                disabled={isLoading || !newComment.trim()}
                className="flex-1 bg-amber-500 text-white py-2 px-4 rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Adding...' : 'Add Comment'}
              </button>
              <button
                onClick={() => setShowCommentModal(false)}
                className="px-4 py-2 border border-amber-300 rounded-lg hover:bg-amber-50 text-amber-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comments List */}
      {enableComments && showComments && comments.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white border border-amber-200 rounded-lg shadow-lg p-3 min-w-80 max-w-md w-full mx-4">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-medium text-amber-800 text-sm">Comments</h4>
            <button
              onClick={() => setShowComments(false)}
              className="text-amber-500 hover:text-amber-700 text-xs"
            >
              <FaTimes />
            </button>
          </div>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {comments.map((comment) => (
              <div key={comment.id} className="bg-amber-25 border border-amber-100 rounded-lg p-3">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <p className="font-medium text-amber-800 text-xs">
                      {comment.userId ? (
                        <button
                          onClick={() => handleArtistNameClick(comment.userId)}
                          onDoubleClick={() => handleArtistNameDoubleClick(comment.userId)}
                          className="hover:text-amber-600 hover:underline transition-colors"
                          title="View artist profile"
                        >
                          {comment.artistName || comment.userName}
                        </button>
                      ) : (
                        comment.artistName || comment.userName
                      )}
                    </p>
                    <p className="text-amber-700 text-xs mt-1">{comment.commentText}</p>
                  </div>
                  <span className="text-xs text-amber-500 ml-2">
                    {formatTimestamp(comment.timestamp)}
                  </span>
                </div>
                
                {/* Comment Actions */}
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-amber-100">
                  <button
                    onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                    className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 transition-colors"
                  >
                    <FaReply className="text-xs" />
                    Reply
                  </button>
                  
                  {/* Delete button - only show if user can delete */}
                  {((user && (user.uid || user.id) === comment.userId) || (user && (user.uid || user.id) === artistId)) && (
                    <button
                      onClick={() => handleDeleteComment(comment.id, comment.userId)}
                      disabled={isLoading}
                      className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                    >
                      <FaTrash className="text-xs" />
                      Delete
                    </button>
                  )}
                </div>

                {/* Reply Form */}
                {replyingTo === comment.id && (
                  <div className="mt-3 pt-3 border-t border-amber-100">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Write a reply..."
                      className="w-full p-2 border border-amber-200 rounded text-xs focus:border-amber-500 focus:outline-none resize-none"
                      rows="2"
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleReply(comment.id)}
                        disabled={isLoading || !replyText.trim()}
                        className="px-3 py-1 bg-amber-500 text-white text-xs rounded hover:bg-amber-600 disabled:opacity-50"
                      >
                        {isLoading ? 'Posting...' : 'Reply'}
                      </button>
                      <button
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyText('');
                        }}
                        className="px-3 py-1 border border-amber-300 text-amber-700 text-xs rounded hover:bg-amber-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Display Replies */}
                {comment.replies && comment.replies.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {comment.replies.map((reply) => (
                      <div key={reply.id} className="ml-4 bg-amber-50 border border-amber-75 rounded p-2">
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex-1">
                            <p className="font-medium text-amber-700 text-xs">
                              {reply.userId ? (
                                <button
                                  onClick={() => handleArtistNameClick(reply.userId)}
                                  className="hover:text-amber-600 hover:underline transition-colors"
                                  title="View artist profile"
                                >
                                  {reply.artistName || reply.userName}
                                </button>
                              ) : (
                                reply.artistName || reply.userName
                              )}
                            </p>
                            <p className="text-amber-600 text-xs mt-1">{reply.commentText}</p>
                          </div>
                          <span className="text-xs text-amber-400 ml-2">
                            {formatTimestamp(reply.timestamp)}
                          </span>
                        </div>
                        
                        {/* Reply Actions */}
                        <div className="flex items-center gap-2 mt-1 pt-1 border-t border-amber-75">
                          {/* Delete button for replies - only show if user can delete */}
                          {((user && (user.uid || user.id) === reply.userId) || (user && (user.uid || user.id) === artistId)) && (
                            <button
                              onClick={() => handleDeleteComment(reply.id, reply.userId)}
                              disabled={isLoading}
                              className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                            >
                              <FaTrash className="text-xs" />
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            </div>
          </div>
        </div>
      )}

    </div>
    )}

      {/* Themed Authentication Modal */}
      <ThemedNotification
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        title="Join the Artmosphere Community!"
        message={authModalMessage}
        type="info"
        showAuthButtons={true}
        onSignUp={() => navigate('/signup')}
        onLogin={() => navigate('/login')}
      />
    </>
  );
};

export default ArtworkReactions; 